import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText,
  Plus,
  Search,
  Filter,
  ArrowLeft,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  Eye
} from 'lucide-react';
import { api } from '@/utils/api';
import { canCreatePlans, getStoredUser } from '@/utils/roleGate';

interface ConstructionPlan {
  id: number;
  projectId: number;
  project: {
    name: string;
    siteAddress: string;
    status: string;
  };
  title: string;
  version: number;
  createdAt: string;
  isActive: boolean;
  signedAt: string | null;
  createdBy: {
    firstName: string;
    lastName: string;
  };
}

export function ConstructionPlansPage() {
  const navigate = useNavigate();
  // Notice passed by the /construction-plans/create route guard when a crew
  // member is redirected here — says WHY they were sent back (never silent).
  const location = useLocation() as { state?: { notice?: string } | null };
  const notice = location.state?.notice;
  // Phase 2b (ROLE-MATRIX-v1): crew is view-only — no "Create New Plan" CTA.
  const canCreate = canCreatePlans(getStoredUser().role);
  const [plans, setPlans] = useState<ConstructionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<string>('active');

  useEffect(() => {
    loadPlans();
  }, [filter]);

  const loadPlans = async () => {
    try {
      const response = await api.get('/construction-plans');
      setPlans(response.data.data || []);
    } catch (error) {
      console.error('Error loading construction plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlans = plans.filter((plan) => {
    const matchesSearch = 
      plan.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.project.siteAddress?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'active') {
      return matchesSearch && plan.isActive;
    }
    
    if (filter === 'signed') {
      return matchesSearch && plan.signedAt !== null;
    }

    if (filter === 'unsigned') {
      return matchesSearch && plan.signedAt === null;
    }
    
    return matchesSearch;
  });

  const activeCount = plans.filter(p => p.isActive).length;
  const signedCount = plans.filter(p => p.signedAt !== null).length;
  const unsignedCount = plans.filter(p => p.signedAt === null).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FileText className="h-12 w-12 text-blue-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading construction plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Redirect notice from the create-page guard (why + what to do) */}
      {notice && (
        <div role="status" className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-6 py-3">
          {notice}
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 flex-shrink-0">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3 truncate">
                  <FileText className="h-8 w-8 text-blue-600 flex-shrink-0" />
                  <span className="truncate">Construction Plans</span>
                </h1>
                <p className="text-gray-600 mt-1 truncate">
                  Rigging calculations, lift plans, and safety documentation
                </p>
              </div>
            </div>
            {canCreate && (
              <Link
                to="/construction-plans/create"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 font-semibold flex-shrink-0"
              >
                <Plus className="h-5 w-5" />
                Create New Plan
              </Link>
            )}
          </div>

          {/* Search and Filters */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by project, site, or plan title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Plans</option>
                <option value="active">Active Plans</option>
                <option value="signed">Signed</option>
                <option value="unsigned">Awaiting Signature</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="container mx-auto px-6 py-6">
        <div className="grid md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Plans</p>
                <p className="text-3xl font-bold text-gray-900">{plans.length}</p>
              </div>
              <FileText className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Plans</p>
                <p className="text-3xl font-bold text-green-600">{activeCount}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Signed</p>
                <p className="text-3xl font-bold text-purple-600">{signedCount}</p>
              </div>
              <CheckCircle className="h-12 w-12 text-purple-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Awaiting Signature</p>
                <p className="text-3xl font-bold text-orange-600">{unsignedCount}</p>
              </div>
              <Clock className="h-12 w-12 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Plans List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project / Site
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPlans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-600" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{plan.title}</div>
                          <div className="text-xs text-gray-500">
                            Created {new Date(plan.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{plan.project.name}</div>
                      <div className="text-sm text-gray-500">{plan.project.siteAddress}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {plan.createdBy.firstName} {plan.createdBy.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">v{plan.version}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {plan.isActive && (
                          <span className="px-2 py-1 text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        )}
                        {plan.signedAt ? (
                          <span className="px-2 py-1 text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            Signed
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                            Unsigned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Link
                          to={`/construction-plans/${plan.id}`}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPlans.length === 0 && (
              <div className="text-center py-12">
                <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-600">No construction plans found</p>
                {canCreate && (
                  <Link
                    to="/construction-plans/create"
                    className="mt-4 inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Create your first plan
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">
                Construction Plans are the Foundation
              </h4>
              <p className="text-sm text-blue-800">
                All construction plans must be created and approved <strong>before</strong> work begins. 
                Daily JHAs will automatically pull data from these plans to streamline safety documentation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

